import BackspaceIcon from '@mui/icons-material/Backspace';
import UndoIcon from '@mui/icons-material/Undo';
import { IconButton, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import {
  orderLineStatusColor,
  PurchaseOrderLineStatus,
  translateOrderLineStatus
} from 'features/inventory/purchase-orders/constants';
import { useTranslation } from 'react-i18next';
import { DataGridCell, baseCell, booleanCell, currencyCell, dateCell } from 'components/cells';
import { filterOperators } from 'components/enhanced';

export function OrderLinesColumns(
  handleDelete?: any,
  onRevertLine?: any,
  sapId?: boolean
): GridColDef[] {
  const { t } = useTranslation();

  const isDisabledEdit = useIsDirector();

  const columns: GridColDef[] = [
    {
      field: 'index',
      headerName: t('global.count'),
      width: 50,
      filterable: false,
      editable: !sapId,
      sortable: false
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      filterable: false,
      sortable: false,
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value;
        return (
          <div>
            <span className={orderLineStatusColor(value)}>
              {translateOrderLineStatus(t, value)}
            </span>
          </div>
        );
      }
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 200,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={params.value['code']} />
      )
    },
    {
      field: 'description',
      headerName: t(`entities.product.description`),
      width: 300,
      filterOperators,
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={params.row.product.description} />
      )
    },
    {
      field: 'manufacturer_id',
      headerName: t(`entities.product.manufacturer_id`),
      width: 160,
      filterOperators,
      valueGetter: (params: GridRenderCellParams<any>) => params.row.product.manufacturer_id ?? '',
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={params.row.product.manufacturer_id ?? ''} />
      )
    },
    {
      field: 'initial_quantity',
      headerName: t(`global.quantity`),
      width: 150,
      type: 'number',
      editable: !isDisabledEdit && handleDelete !== false && !sapId,
      renderCell: baseCell
    },
    {
      field: 'remaining_quantity',
      headerName: t(`entities.order_line.remaining_quantity`),
      width: 150,
      type: 'number',
      renderCell: baseCell
    },
    {
      field: 'discounted',
      headerName: t('entities.movement.discounted'),
      width: 100,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'amount',
      headerName: t('entities.order_line.amount'),
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'iva',
      headerName: t('entities.order_line.iva'),
      width: 75,
      renderCell: baseCell
    },
    {
      field: 'requested_shipment_date',
      headerName: t(`entities.purchase_request.requested_shipment_date`),
      width: 200,
      filterOperators,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      editable: handleDelete !== false,
      renderCell: dateCell
    },
    {
      field: 'notes',
      headerName: t(`entities.purchase_request.notes`),
      width: 200,
      editable: !isDisabledEdit && handleDelete !== false,
      renderCell: baseCell
    }
  ];
  if (handleDelete && !isDisabledEdit && !sapId) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      width: 150,
      getActions: (params: GridRowParams) => [
        <Tooltip title={t('actions.delete')} placement="top" key="delete">
          <IconButton onClick={() => handleDelete(params.row)} color="error">
            <BackspaceIcon fontSize="small" />
          </IconButton>
        </Tooltip>,
        ...(onRevertLine &&
        params.row.status_code === PurchaseOrderLineStatus.IN_ARRIVO &&
        params.row.remaining_quantity === params.row.initial_quantity
          ? [
              <Tooltip
                title={t('pages.purchase-orders.view.revert-status-action')}
                placement="top"
                key="revert">
                <IconButton onClick={() => onRevertLine(params.row)}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ]
          : [])
      ]
    } as GridColDef);
  }
  return columns;
}
